import React from 'react';
import classNames from 'classnames';
import { TranslationGetter } from '@wix/editor-elements-types';
import {
  MediaContainerCompProps,
  MediaContainerVideoAPI,
} from '../MediaContainer.types';
import {
  ARIA_LABEL_DEFAULT,
  ARIA_LABEL_KEY,
  ARIA_LABEL_NAMESPACE,
} from '../../../Container/viewer/shared/constants';
import { useVideoAPI } from '../../../../core/corvid/media/backgroundUtils';
import MediaContainerContent from './MediaContainerContent';
import styles from './styles/MediaContainer.scss';

type A11yAttr = {
  role?: string;
  'aria-label'?: string;
  tabindex?: number;
};

const getAriaLabel = (translate: TranslationGetter | undefined) => {
  return translate
    ? translate(ARIA_LABEL_NAMESPACE, ARIA_LABEL_KEY, ARIA_LABEL_DEFAULT)
    : ARIA_LABEL_DEFAULT;
};

const getA11yAttr = (
  hasHoverInteraction?: boolean,
  shouldAddTabIndex0?: boolean,
  translate?: TranslationGetter,
) => {
  let attr: A11yAttr = {};

  if (hasHoverInteraction) {
    attr = { role: 'region', 'aria-label': getAriaLabel(translate) };

    if (shouldAddTabIndex0) {
      attr.tabindex = 0;
    }
  }

  return attr;
};

const MediaContainer: React.ForwardRefRenderFunction<
  MediaContainerVideoAPI,
  MediaContainerCompProps
> = (props: MediaContainerCompProps, compRef) => {
  const {
    id,
    children,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    hasHoverInteraction,
    shouldAddTabIndex0,
    hasPlatformClickHandler,
    translate,
    fillLayers,
    onStop,
  } = props;

  const className = classNames(styles.mediaContainer, {
    [styles.clickable]: hasPlatformClickHandler,
  });

  const a11yAttr = getA11yAttr(
    hasHoverInteraction,
    shouldAddTabIndex0,
    translate,
  );

  const hasVideo = !!fillLayers.video;
  const videoRef = useVideoAPI(compRef, hasVideo, onStop);

  return (
    <div
      id={id}
      className={className}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...a11yAttr}
    >
      <MediaContainerContent {...props} videoRef={videoRef}>
        {children}
      </MediaContainerContent>
    </div>
  );
};

export default React.forwardRef(MediaContainer);
